import React, { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ActiveBuffs.css';

const EMPTY_ARRAY = [];

// Icons for different buff types
const BuffIcons = {
  zap: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  building: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="10" width="4" height="11" rx="1" /><rect x="10" y="5" width="4" height="16" rx="1" /><rect x="17" y="1" width="4" height="20" rx="1" />
    </svg>
  ),
  star: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
};

// Format remaining time nicely
function formatTimeRemaining(seconds) {
  if (seconds <= 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
  } else if (minutes > 0) {
    return `${minutes}m${secs > 0 ? ` ${secs}s` : ''}`;
  }
  return `${secs}s`;
}

// Single buff item in dropdown
const BuffItem = memo(({ buff, remainingSeconds }) => {
  const IconComponent = BuffIcons[buff.icon] || BuffIcons.zap;
  const isXpBuff = buff.buffType === 'xp_bonus';
  const isExpiring = remainingSeconds <= 60; // Less than 1 minute

  return (
    <div className={`buff-item ${isXpBuff ? 'buff-xp' : 'buff-revenue'} ${isExpiring ? 'expiring' : ''}`}>
      <div className="buff-item-icon">
        <IconComponent />
      </div>
      <div className="buff-item-info">
        <span className="buff-item-name">{buff.name}</span>
        <span className="buff-item-desc">{buff.description}</span>
      </div>
      <div className="buff-item-stats">
        <span className="buff-item-value">+{buff.value}%</span>
        <span className="buff-item-timer">{formatTimeRemaining(remainingSeconds)}</span>
      </div>
    </div>
  );
});

// Main ActiveBuffs component
const ActiveBuffs = memo(({ buffs = EMPTY_ARRAY }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [buffTimers, setBuffTimers] = useState({});

  // Filter out expired buffs
  const activeBuffs = buffs.filter(buff => {
    const remaining = buffTimers[buff.id] ?? buff.remainingSeconds;
    return remaining > 0;
  });

  // Initialize and update timers
  useEffect(() => {
    // Initialize timers from buff data
    const initialTimers = {};
    buffs.forEach(buff => {
      initialTimers[buff.id] = buff.remainingSeconds;
    });
    setBuffTimers(initialTimers);

    // Update every second
    const interval = setInterval(() => {
      setBuffTimers(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        Object.keys(updated).forEach(id => {
          if (updated[id] > 0) {
            updated[id] = updated[id] - 1;
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [buffs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('.active-buffs-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  if (activeBuffs.length === 0) {
    return null;
  }

  // Get primary buff for button display
  const primaryBuff = activeBuffs[0];
  const primaryRemaining = buffTimers[primaryBuff.id] ?? primaryBuff.remainingSeconds;
  const isXpBuff = primaryBuff.buffType === 'xp_bonus';

  return (
    <div className="active-buffs-container">
      <button
        className={`active-buffs-btn ${isXpBuff ? 'buff-xp' : 'buff-revenue'} ${activeBuffs.length > 1 ? 'multiple' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={t('active_boosts')}
      >
        <div className="buffs-btn-icon">
          {BuffIcons[primaryBuff.icon] ? BuffIcons[primaryBuff.icon]() : BuffIcons.zap()}
        </div>
        <div className="buffs-btn-info">
          <span className="buffs-btn-value">+{primaryBuff.value}%</span>
          <span className="buffs-btn-timer">{formatTimeRemaining(primaryRemaining)}</span>
        </div>
        {activeBuffs.length > 1 && (
          <span className="buffs-count">+{activeBuffs.length - 1}</span>
        )}
      </button>

      {isOpen && (
        <div className="buffs-dropdown">
          <div className="buffs-dropdown-header">
            <span>{t('active_boosts')}</span>
          </div>
          <div className="buffs-dropdown-list">
            {activeBuffs.map(buff => (
              <BuffItem
                key={buff.id}
                buff={buff}
                remainingSeconds={buffTimers[buff.id] ?? buff.remainingSeconds}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default ActiveBuffs;
